import type { NavItem, NavSection } from "@skypointcloud/skypoint-ui";
import type {
  SettingsTileGroupView,
  SettingsTileView,
} from "../../../components/shared/SettingsTiles";
import { getNavRoute, HUB_ROUTES, isHubId, isSettingsHubId, navIdsOwning } from "./routeMapping";
import type { NavId } from "./sidebarConfig";

/**
 * One Settings item per product, opening a tiled Settings page
 * (docs/superpowers/specs/2026-10-08-unified-shell-settings-hub-design.md, D5).
 *
 * THE ONE SOURCE. buildNavSections keeps producing each product's settings entries as the children
 * of its Settings item, gated exactly as before (permission, feature and staff gates). Everything
 * here reads those children, never a gate of its own:
 *
 * - the sidebar draws the Settings item without them (withSettingsCollapsed);
 * - the route guard admits each entry's route, as it always has, plus the tiled page itself for
 *   whoever is drawn the item (routeGuardRules);
 * - the tiled page shows a tile for each entry and nothing else (visibleSettingsTiles);
 * - "Back to Settings" shows on an entry's page (settingsBackTarget).
 *
 * So the tiles, the sidebar and the guard cannot drift: a tile is shown exactly when the guard
 * admits the page it opens.
 *
 * Hire's Templates item (Templates & Setup) is a hub of the same kind (HUB_ROUTES): its children are
 * the template pages, and the drawer, the highlight, the guard and the back link treat it exactly as
 * they treat Settings. Only the Settings-named helpers (settingsHubItemOf, settingsEntryIdsOf) read
 * the Settings item alone.
 */

/** The drawn Settings item that holds a product's settings entries (at most one per sidebar). */
export function settingsHubItemOf(sections: readonly NavSection[]): NavItem | undefined {
  return sections.flatMap((section) => section.items).find((item) => isSettingsHubId(item.id));
}

/** Every drawn item that opens a tiled page (HUB_ROUTES): Settings, and in Hire, Templates. */
export function hubItemsOf(sections: readonly NavSection[]): NavItem[] {
  return sections.flatMap((section) => section.items).filter((item) => isHubId(item.id));
}

/** The settings entries the sidebar data holds for the caller, in order. */
export function settingsEntryIdsOf(sections: readonly NavSection[]): string[] {
  return (settingsHubItemOf(sections)?.children ?? []).map((child) => child.id);
}

/**
 * The sections as the drawer draws them: each hub item (Settings, Templates) without its entries,
 * so it is one item that opens its tiled page instead of a group. Every other item is untouched.
 */
export function withSettingsCollapsed(sections: readonly NavSection[]): NavSection[] {
  return sections.map((section) =>
    section.items.some((item) => isHubId(item.id))
      ? {
          ...section,
          items: section.items.map((item) => {
            if (!isHubId(item.id)) return item;
            const { children: _entries, ...collapsed } = item;
            return collapsed;
          }),
        }
      : section,
  );
}

interface Selection {
  selectedId: string | undefined;
  selectedCategoryId: string | undefined;
}

/** Whether `pathname` belongs to one of the Settings item's entries (its own pattern match). */
function entryOwning(pathname: string, queryString: string, entries: ReadonlySet<string>) {
  return navIdsOwning(pathname, queryString).find((id) => entries.has(id));
}

/**
 * The highlight on the collapsed drawer: a hub item (Settings, Templates) on its tiled page and on
 * every page one of its entries owns, the shared pages (Users, Products) in another product's shell
 * included. Anything else keeps `selection` unchanged.
 */
export function settingsHubSelection(
  pathname: string,
  queryString: string,
  selection: Selection,
  sections: readonly NavSection[],
): Selection {
  for (const hub of hubItemsOf(sections)) {
    const entries = new Set((hub.children ?? []).map((child) => child.id));
    const owned =
      selection.selectedId === hub.id ||
      selection.selectedCategoryId === hub.id ||
      (selection.selectedId !== undefined && entries.has(selection.selectedId)) ||
      entryOwning(pathname, queryString, entries) !== undefined;
    if (owned) return { selectedId: hub.id, selectedCategoryId: undefined };
  }
  return selection;
}

/** Where a back link goes, and the hub it is named for ("Settings", "Templates"). */
export interface HubBackLink {
  route: string;
  label: string;
}

/**
 * The back link on this page, or null when it shows none: "Back to Settings" on a settings entry's
 * own page and the pages beneath it (`/settings/candidate-engagement/rules`, the tabbed page's tabs),
 * and "Back to Templates" on a template page and the pages beneath it (`/templates/feedback/new`);
 * not on a tiled page itself, and not on a page an entry merely owns elsewhere (an integration's own
 * page, which has its own way back to Integrations).
 *
 * The page is an entry's own when it sits under that entry's route, or under the route of an id tied
 * with it for the page: `/templates/sms` is the Email & SMS Templates entry's page, routed as the SMS
 * id it shares the page with.
 */
export function hubBackLink(pathname: string, sections: readonly NavSection[]): HubBackLink | null {
  for (const hub of hubItemsOf(sections)) {
    const hubRoute = HUB_ROUTES[hub.id as NavId];
    if (!hubRoute) continue;
    if (pathname === hubRoute) return null;
    const entries = new Set((hub.children ?? []).map((child) => child.id));
    const owners = navIdsOwning(pathname);
    if (!owners.some((id) => entries.has(id))) continue;
    const beneath = owners.some((id) => {
      const route = getNavRoute(id as NavId);
      return route !== undefined && (pathname === route || pathname.startsWith(route + "/"));
    });
    return beneath ? { route: hubRoute, label: hub.label } : null;
  }
  return null;
}

/** Where the back link goes on this page (hubBackLink), or null when it shows none. */
export function settingsBackTarget(
  pathname: string,
  sections: readonly NavSection[],
): string | null {
  return hubBackLink(pathname, sections)?.route ?? null;
}

/** A tile's icon: a lucide icon, as the sidebar's. */
export type SettingsTileIcon = NonNullable<SettingsTileView["icon"]>;

/** A tile on a tiled Settings page: one settings entry, or one page of it (the tabbed page's tabs). */
export interface SettingsTile {
  /** The settings entry (a child of the Settings item) whose presence shows the tile. */
  navId: string;
  /** Distinguishes tiles of one entry; defaults to navId. */
  key?: string;
  /** Defaults to the entry's sidebar label, so the tile is titled as the page always was. */
  title?: string;
  /** One line. */
  description: string;
  /** Defaults to the entry's sidebar icon. */
  icon?: SettingsTileIcon;
  /** The page it opens, without the query; defaults to the entry's route. */
  path?: string;
}

export interface SettingsTileGroup {
  key: string;
  title: string;
  /** One line under the heading. */
  blurb: string;
  tiles: readonly SettingsTile[];
}

export type VisibleTile = SettingsTileView;
export type VisibleTileGroup = SettingsTileGroupView & { tiles: VisibleTile[] };

/**
 * The groups and tiles to show: a tile whose entry the caller's hub item holds (the Settings item,
 * or the item `hubId` names, e.g. Templates), and a group only when it has a tile left. Nothing else
 * decides (see THE ONE SOURCE above).
 */
export function visibleSettingsTiles(
  groups: readonly SettingsTileGroup[],
  sections: readonly NavSection[],
  hubId?: NavId,
): VisibleTileGroup[] {
  const hub =
    hubId === undefined
      ? settingsHubItemOf(sections)
      : hubItemsOf(sections).find((item) => item.id === hubId);
  const entries = new Map((hub?.children ?? []).map((child) => [child.id, child]));
  return groups
    .map((group) => ({
      key: group.key,
      title: group.title,
      blurb: group.blurb,
      tiles: group.tiles.flatMap((tile) => {
        const entry = entries.get(tile.navId);
        if (!entry) return [];
        return [
          {
            key: tile.key ?? tile.navId,
            title: tile.title ?? entry.label,
            description: tile.description,
            icon: tile.icon ?? entry.icon,
            path: tile.path ?? getNavRoute(tile.navId as NavId),
          },
        ];
      }),
    }))
    .filter((group) => group.tiles.length > 0);
}
